import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, History, Quote, AlertTriangle } from "lucide-react";

interface WikiDoc {
  id: string; slug: string; title: string; status: string;
  category: string; updated_at: string; current_version_id: string | null;
}
interface WikiVersion {
  id: string; document_id: string; version_number: number;
  published: boolean; published_at: string | null; created_at: string; summary: string | null;
}
interface WikiCitationRow {
  document_id: string; cnt: number; slug: string;
}
interface WikiLintRow {
  id: string; rule_key: string; severity: string; document_id: string | null;
  details: Record<string, unknown>; resolved: boolean; created_at: string;
}

export default function WikiHub() {
  const [tab, setTab] = useState("docs");
  const [docs, setDocs] = useState<WikiDoc[]>([]);
  const [versions, setVersions] = useState<WikiVersion[]>([]);
  const [citations, setCitations] = useState<WikiCitationRow[]>([]);
  const [lints, setLints] = useState<WikiLintRow[]>([]);

  useEffect(() => {
    (async () => {
      const { data: d } = await supabase.from("wiki_documents").select("*")
        .order("updated_at", { ascending: false }).limit(100);
      setDocs((d as WikiDoc[]) ?? []);
      const { data: v } = await supabase.from("wiki_document_versions").select("*")
        .order("created_at", { ascending: false }).limit(50);
      setVersions((v as WikiVersion[]) ?? []);
      const { data: c } = await supabase
        .from("wiki_citations")
        .select("document_id")
        .gte("created_at", new Date(Date.now() - 7 * 86400000).toISOString());
      const counts = new Map<string, number>();
      (c ?? []).forEach((r: { document_id: string | null }) => {
        if (!r.document_id) return;
        counts.set(r.document_id, (counts.get(r.document_id) ?? 0) + 1);
      });
      const slugMap = new Map<string, string>();
      (d as WikiDoc[] | null ?? []).forEach((x) => slugMap.set(x.id, x.slug));
      setCitations(
        Array.from(counts.entries())
          .map(([document_id, cnt]) => ({ document_id, cnt, slug: slugMap.get(document_id) ?? "—" }))
          .sort((a, b) => b.cnt - a.cnt)
          .slice(0, 20)
      );
      const { data: l } = await supabase.from("wiki_lint_runs").select("*")
        .eq("resolved", false).order("created_at", { ascending: false }).limit(100);
      setLints((l as WikiLintRow[]) ?? []);
    })();
  }, []);

  return (
    <div className="container mx-auto px-4 py-4 max-w-7xl">
      <div className="mb-4">
        <h1 className="text-2xl font-bold font-headline tracking-tight">Wiki Documental</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          5ª camada de memória — políticas, FAQs e conhecimento canônico citável.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="docs" className="gap-2"><BookOpen className="w-4 h-4" />Documentos</TabsTrigger>
          <TabsTrigger value="versions" className="gap-2"><History className="w-4 h-4" />Versões</TabsTrigger>
          <TabsTrigger value="citations" className="gap-2"><Quote className="w-4 h-4" />Citações</TabsTrigger>
          <TabsTrigger value="lint" className="gap-2"><AlertTriangle className="w-4 h-4" />Lint</TabsTrigger>
        </TabsList>

        <TabsContent value="docs" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Documentos ({docs.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Slug</TableHead><TableHead>Título</TableHead>
                  <TableHead>Categoria</TableHead><TableHead>Status</TableHead>
                  <TableHead>Atualizado</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-mono text-xs">{d.slug}</TableCell>
                      <TableCell>{d.title}</TableCell>
                      <TableCell><Badge variant="outline">{d.category}</Badge></TableCell>
                      <TableCell>
                        <Badge variant={d.status === "published" ? "default" : "secondary"}>{d.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(d.updated_at).toLocaleString("pt-BR")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="versions" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Versões recentes ({versions.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Doc</TableHead><TableHead>v#</TableHead>
                  <TableHead>Publicada</TableHead><TableHead>Resumo</TableHead><TableHead>Criada</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {versions.map((v) => {
                    const doc = docs.find((d) => d.id === v.document_id);
                    return (
                      <TableRow key={v.id}>
                        <TableCell className="font-mono text-xs">{doc?.slug ?? "—"}</TableCell>
                        <TableCell>v{v.version_number}</TableCell>
                        <TableCell>
                          {v.published
                            ? <Badge variant="default">sim</Badge>
                            : <Badge variant="secondary">draft</Badge>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                          {v.summary ?? "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(v.created_at).toLocaleString("pt-BR")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="citations" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Top citações (últimos 7 dias)</CardTitle></CardHeader>
            <CardContent>
              {citations.length === 0
                ? <p className="text-sm text-muted-foreground">Sem citações no período. O pipeline ainda não consultou a Wiki nas respostas.</p>
                : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Documento</TableHead><TableHead>Citações</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {citations.map((c) => (
                        <TableRow key={c.document_id}>
                          <TableCell className="font-mono text-xs">{c.slug}</TableCell>
                          <TableCell><Badge>{c.cnt}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lint" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Achados abertos ({lints.length})</CardTitle></CardHeader>
            <CardContent>
              {lints.length === 0
                ? <p className="text-sm text-muted-foreground">Nenhum achado pendente. O linter roda aos domingos.</p>
                : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Regra</TableHead><TableHead>Severidade</TableHead>
                      <TableHead>Documento</TableHead><TableHead>Detalhes</TableHead><TableHead>Quando</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {lints.map((l) => {
                        const doc = docs.find((d) => d.id === l.document_id);
                        return (
                          <TableRow key={l.id}>
                            <TableCell className="font-mono text-xs">{l.rule_key}</TableCell>
                            <TableCell>
                              <Badge variant={l.severity === "high" ? "destructive" : l.severity === "medium" ? "default" : "secondary"}>
                                {l.severity}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{doc?.slug ?? "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                              {JSON.stringify(l.details)}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {new Date(l.created_at).toLocaleString("pt-BR")}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
